import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../config/api';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for stored user session and tokens
    const storedUser = localStorage.getItem('currentUser');
    const storedToken = localStorage.getItem('token');
    const storedRefreshToken = localStorage.getItem('refreshToken');
    
    if (storedUser && storedToken) {
      setUser(JSON.parse(storedUser));
      setToken(storedToken);
      setRefreshToken(storedRefreshToken);
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      const response = await fetch(`${API_CONFIG.FULL_API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-application-type': 'bidmanager-admin-panel'
        },
        body: JSON.stringify({
          user_name: username,
          password: password
        })
      });

      const data = await response.json();

      // If verification is required (no token but success = true)
      // Check for requiresVerification flag OR if success=true but no token
      if (data.success && (data.requiresVerification || (!data.token && (data.userId || data.user_id || data.id)))) {
        const userId = data.userId || data.user_id || data.id;
        return { 
          success: true, 
          requiresVerification: true,
          userId: userId,
          message: data.message || 'Verification code sent to your email'
        };
      }

      if (data.success && data.token) {
        const userWithoutPassword = data.user;
        
        // Store everything
        setUser(userWithoutPassword);
        setToken(data.token);
        setRefreshToken(data.refreshToken);
        
        localStorage.setItem('currentUser', JSON.stringify(userWithoutPassword));
        localStorage.setItem('token', data.token);
        localStorage.setItem('refreshToken', data.refreshToken);
        
        return { success: true, user: userWithoutPassword };
      }

      return { success: false, error: data.error || 'Invalid credentials' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Connection error. Please try again.' };
    }
  };

  const completeLogin = (authData) => {
    const userWithoutPassword = authData.user;
    
    // Store everything
    setUser(userWithoutPassword);
    setToken(authData.token);
    setRefreshToken(authData.refreshToken);
    
    localStorage.setItem('currentUser', JSON.stringify(userWithoutPassword));
    localStorage.setItem('token', authData.token);
    localStorage.setItem('refreshToken', authData.refreshToken);
  };

  const logout = async () => {
    try {
      // Call logout API
      if (token) {
        await fetch(`${API_CONFIG.FULL_API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'x-application-type': 'bidmanager-admin-panel'
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    }

    // Clear everything
    setUser(null);
    setToken(null);
    setRefreshToken(null);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    navigate('/login');
  };

  const refreshAccessToken = async () => {
    try {
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${API_CONFIG.FULL_API_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-application-type': 'bidmanager-admin-panel'
        },
        body: JSON.stringify({ refreshToken })
      });

      const data = await response.json();

      if (data.token) {
        setToken(data.token);
        localStorage.setItem('token', data.token);
        return data.token;
      }

      throw new Error('Failed to refresh token');
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      return null;
    }
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.permissions && user.permissions.includes('all')) return true;
    return user.permissions && user.permissions.includes(permission);
  };

  const canManageUser = (targetUser) => {
    if (!user) return false;
    
    // Super admin can manage everyone
    if (user.role === 'super_admin') return true;
    
    // Manager can manage dealers and subdealers in their branch
    // Backend uses user_id (e.g. USER176...) for managerId; match all possible current user ids
    if (user.role === 'manager') {
      const myManagerIds = [user.user_id, user.managerId, user.id, user.autoIncrementId].filter(Boolean);
      const branchMatch = targetUser.branchId != null && user.branchId != null &&
        Number(targetUser.branchId) === Number(user.branchId);
      const managerMatch = myManagerIds.some(id => String(targetUser.managerId) === String(id));
      return (targetUser.role === 'dealer' || targetUser.role === 'subdealer') && branchMatch && managerMatch;
    }
    
    // Dealer can manage their own subdealers
    if (user.role === 'dealer') {
      return targetUser.role === 'subdealer' && 
             targetUser.dealerId === user.dealerId;
    }
    
    // Subdealers can't manage anyone
    return false;
  };

  const canCreateDealer = () => {
    if (!user) return false;
    // Only super admin and managers can create dealers
    return user.role === 'super_admin' || user.role === 'manager';
  };

  const canCreateSubdealer = () => {
    if (!user) return false;
    // Super admin, managers, and dealers can create subdealers
    return user.role === 'super_admin' || user.role === 'manager' || user.role === 'dealer';
  };

  const canManageBranch = (branchId) => {
    if (!user) return false;
    
    // Super admin can manage all branches
    if (user.role === 'super_admin') return true;
    
    // Managers can only view their own branch
    if (user.role === 'manager') {
      return branchId === user.branchId;
    }
    
    return false;
  };

  const getUserBranch = () => {
    if (!user) return null;
    if (user.role === 'super_admin') return null;
    // Branch data should come from API, not mock data
    return user.branchName ? { id: user.branchId, name: user.branchName } : null;
  };

  const value = {
    user,
    token,
    refreshToken,
    loading,
    login,
    completeLogin,
    logout,
    refreshAccessToken,
    hasPermission,
    canManageUser,
    canManageBranch,
    canCreateDealer,
    canCreateSubdealer,
    getUserBranch,
    isAuthenticated: !!token,
    // Role helper methods
    isSuperAdmin: user?.role === 'super_admin',
    isManager: user?.role === 'manager',
    isDealer: user?.role === 'dealer',
    isSubdealer: user?.role === 'subdealer'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
